import { Bound } from '@blocksuite/global/gfx';
import last from 'lodash-es/last';

import type { PointerEventState } from '../../event';
import type { GfxController } from '../controller.js';
import type { GfxElementModelView, SupportedEvent } from '../view/view.js';
import { ativa as sondaAtiva, marcar as marcarNaSonda, pintar as pintarSonda } from './tuninho/sonda-do-toque.js';

export class GfxViewEventManager {
  private _hoveredElementsStack: GfxElementModelView[] = [];
  private _draggingElement: GfxElementModelView | null = null;

  private _callInReverseOrder(
    callback: (view: GfxElementModelView) => void,
    arr = this._hoveredElementsStack
  ) {
    for (let i = arr.length - 1; i >= 0; i--) {
      const view = arr[i];

      callback(view);
    }
  }

  constructor(private readonly gfx: GfxController) {}

  dispatch(eventName: SupportedEvent, evt: PointerEventState) {
    if (eventName === 'pointermove') {
      this._handlePointerMove(evt);
      return false;
    } else if (eventName.startsWith('drag')) {
      return this._handleDrag(
        eventName as 'dragstart' | 'dragend' | 'dragmove',
        evt
      );
    } else {
      // tuninho (card 1864882251313972786): a pilha só é preenchida por `pointermove`, e um
      // TOQUE pode ir de `pointerdown` a `pointerup` sem nenhum — medido no Mirezi do `dev` em
      // 2026-09-15: dois toques sobre um elemento produziram `pointerdown/up/click` e zero
      // `pointermove`. Sem este recurso, `doubleClick` passa a ser emitido (a correção do
      // `ClickController`) e é entregue a `undefined`: o gesto existe, não chega a ninguém, e
      // nada falha — o modo de falha que a Regra 8.9 chama de "default que finge".
      //
      // Quando a pilha está cheia (todo caminho de mouse, e o dedo que chega a se mover), este
      // ramo não roda e o comportamento é exatamente o de antes.
      const alvo =
        last(this._hoveredElementsStack) ?? last(this._elementosNoPonto(evt));
      if (eventName === 'dblclick' && sondaAtiva()) {
        // tuninho (card 1864882251313972786): a sonda mede as DUAS fontes separadamente — a
        // pilha de hover ter conteudo muda qual ramo do `??` decide, e no toque ela COSTUMA
        // estar vazia. "Costuma" nao e medicao, e foi essa suposicao que a instrumentacao
        // autonoma nao conseguiu responder (ela nao escuta `pointermove`).
        //
        // ⚠️ O `_elementosNoPonto` e chamado AQUI DENTRO, nao acima. Subi-lo para fora do `??`
        // tiraria o curto-circuito e o faria rodar em TODO clique de mouse — uma busca no grid
        // por gesto, em caminho quente, por causa de um painel de diagnostico. Aqui ele custa
        // uma segunda busca so no duplo clique, e so com a sonda ligada.
        const noPonto = this._elementosNoPonto(evt);
        marcarNaSonda('GfxViewEventManager achou alvo', Boolean(alvo),
          `hover=${this._hoveredElementsStack.length} noPonto=${noPonto.length}` +
          (alvo ? ` tipo=${(alvo as { constructor: { name: string } }).constructor.name}` : ''));
        pintarSonda();
      }
      return alvo?.dispatch(eventName, evt);
    }
  }

  private _handleDrag(
    evtName: 'dragstart' | 'dragend' | 'dragmove',
    _evt: PointerEventState
  ): boolean {
    switch (evtName) {
      case 'dragstart': {
        if (this._draggingElement) {
          this._draggingElement.dispatch('dragend', _evt);
        }
        this._draggingElement = last(this._hoveredElementsStack) ?? null;
        return this._draggingElement?.dispatch('dragstart', _evt) ?? false;
      }
      case 'dragmove': {
        return this._draggingElement?.dispatch('dragmove', _evt) ?? false;
      }
      case 'dragend': {
        const dispatched =
          this._draggingElement?.dispatch('dragend', _evt) ?? false;
        this._draggingElement = null;
        return dispatched;
      }
    }
  }

  /**
   * Quais visões de elemento estão sob o ponto deste evento.
   *
   * tuninho (card 1864882251313972786): isto era o corpo de `_handlePointerMove`. Virou método
   * próprio para que o recurso do `dispatch` — quando não houve `pointermove` nenhum, como no
   * toque — use **exatamente o mesmo critério de acerto**, e não um paralelo que envelhece em
   * separado (Regra #9). O `_handlePointerMove` continua sendo o único que mexe na pilha.
   */
  private _elementosNoPonto(_evt: PointerEventState): GfxElementModelView[] {
    const [x, y] = this.gfx.viewport.toModelCoord(_evt.x, _evt.y);
    return this.gfx.grid
      .search(new Bound(x - 5, y - 5, 10, 10), {
        filter: ['canvas', 'local'],
      })
      .reduce((pre, model) => {
        if (
          model.includesPoint(
            x,
            y,
            {
              hitThreshold: 10,
              responsePadding: [5, 5],
            },
            this.gfx.std.host
          ) ||
          ('externalBound' in model
            ? model.externalBound?.isPointInBound([x, y])
            : false)
        ) {
          const view = this.gfx.view.get(model) as GfxElementModelView | null;

          view && pre.push(view);
        }

        return pre;
      }, [] as GfxElementModelView[]);
  }

  private _handlePointerMove(_evt: PointerEventState): void {
    const hoveredElmViews = this._elementosNoPonto(_evt);

    const currentStackedViews = new Set(this._hoveredElementsStack);
    const visited = new Set<GfxElementModelView>();

    this._callInReverseOrder(view => {
      if (currentStackedViews.has(view)) {
        visited.add(view);
        view.dispatch('pointermove', _evt);
      } else {
        view.dispatch('pointerenter', _evt);
      }
    }, hoveredElmViews);
    this._callInReverseOrder(
      view => !visited.has(view) && view.dispatch('pointerleave', _evt)
    );
    this._hoveredElementsStack = hoveredElmViews;
  }
}
